import { AttachmentOverflow } from "src/components/llm/components/attachment-overflow";
import { ComposerFiles } from "src/components/llm/components/composer-files";
import {
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Column,
  PromptInputSubmitButton,
  Row,
  Tooltip,
  chakra,
  TextWithTooltip,
} from "@hightouch/htui";
import { useReducedMotion } from "motion/react";
import type { ComposerFile } from "src/components/llm/utils/file-processing";
import { textShimmer } from "src/components/llm/components/loading";
import { BasePill } from "src/components/llm/components/composer-file-pill";
import { handleAltEnterNewline } from "src/utils/chat-newline-shortcut";
import {
  ComposerLeftActions,
  EMPTY_ATTACHMENT_SOURCES,
  buildAddFilesAction,
  type ComposerLeftAction,
} from "./composer-left-actions";
import {
  TEXTAREA_LINE_HEIGHT_PX,
  MIN_INLINE_TEXT_WIDTH_PX,
  ELEVATED_WRAPPER_RADIUS_PX,
  ELEVATED_WRAPPER_PADDING_PX,
  COMPOSER_BOX_RADIUS_PX,
  COMPOSER_BOX_ATTR,
  COMPOSER_BOX_SELECTOR,
  COMPOSER_LAYOUT_ATTR,
  COMPOSER_RING_ATTR,
  LIT_BOX,
  getSingleLineTextWidth,
  getUnclippedWidth,
} from "./compact-chat-input-layout";
import {
  type AttachmentSource,
  useAttachmentSources,
} from "./attachment-source";
import { useComposerAttachments } from "./use-composer-attachments";
import { ComposerToolbarScroller } from "src/components/llm/components/composer-toolbar-scroller";
import {
  ComposerToolbar,
  COMPACT_TOOLBAR_WIDTH,
  useCompactToolbarViewport,
} from "src/components/llm/components/composer-toolbar";
import { LlmDisclaimer } from "./disclaimer";
import {
  useImageGalleryOverlay,
  type GalleryImage,
} from "./full-screen-image-overlay";

import type { SubmitOverride as HoistSubmitOverride } from "./use-active-interactive-hoist";
import { getModeTransitionTooltip } from "src/components/llm/messages/mode-transition-utils";
import {
  type ComposerContextChip,
  ComposerContextChipRow,
} from "src/components/llm/components/composer-context-chip";

import { useComposerDictation } from "src/components/llm/hooks/use-composer-dictation";
import {
  DictationControl,
  DictationToolbar,
} from "src/components/llm/components/dictation-control";

import type { ContextAttachment } from "src/pages/agents/types";

export interface ContextItem {
  id: string;
  label: string;
  /** 24px thumbnail always mounted in the pill. */
  thumbnail: ReactNode;
  /** Larger preview shown in the hover popover. Rendered lazily. */
  popoverPreview: ReactNode;
  /** Image URL for rendering in the chat message history via ImageThumbnail. */
  imageSrc?: string;
  /** Sent-message preview for context that is not a bitmap. */
  messageAttachment?: ContextAttachment;
  /** Null excludes this item from referenceAssetIds; omitted uses the item id. */
  referenceAssetId?: string | null;
}

/** Hoist hook owns the canonical SubmitOverride type. The input doesn't need
 * the `hoistedMessageKey` (that's consumed by the message list). */
export type SubmitOverride = Omit<HoistSubmitOverride, "hoistedMessageKey">;

interface CompactChatInputProps {
  placeholder?: string;
  onSubmit: (value: string, files: ComposerFile[]) => void;
  /**
   * Optional gate run immediately before submitting. Return false to cancel:
   * `onSubmit` is not called and the input keeps its text and files, so the
   * user doesn't lose what they typed. Use for required selections that must be
   * made before sending (e.g. choosing a product surface).
   */
  validateSubmit?: () => boolean;
  autoFocus?: boolean;
  variant?: "default" | "elevated";
  dictationContextKey?: string;
  isLoading?: boolean;
  onCancel?: () => void;
  actions?: ReactNode;
  /** Content seated beside the built-in "+" control, on the input's left. */
  leftActions?: ReactNode;
  /** When true, actions remain visible even before the input is focused or populated. */
  showActionsWhenIdle?: boolean;
  loadingPreviewText?: string;
  /** Controls the inner prompt bg. Default "#f5f5f5", switches to white on hover/focus. */
  promptBg?: string;
  /** Content rendered directly above the input, visually attached (e.g. upsell banner) */
  banner?: ReactNode;
  /** Content inside the input, above the text, such as attached templates. */
  topContent?: ReactNode;
  /** Removable chips for what the next message sends, above the text. */
  contextChips?: readonly ComposerContextChip[];
  /**
   * Fires when the input becomes (or stops being) eligible to show prompt
   * chips: focused, empty, not loading, not processing files. Consumers render
   * the chip UI externally so it can float above the input.
   */
  onPromptChipsReadyChange?: (ready: boolean) => void;
  /**
   * Fires when the input's "active" state changes — active meaning focused or
   * holding content (typed text, attached files, or context items). Consumers
   * that render their own outer ring (e.g. the collapsed overlay) use this to
   * flip that ring to its focused styling and keep it there while content
   * remains, mirroring the `elevated` variant's built-in wrapper.
   */
  onActiveChange?: (active: boolean) => void;
  /** When true, the input and submit button are disabled. */
  isDisabled?: boolean;
  /** When set, blocks submission and shows this message as a tooltip on the submit button. */
  submitDisabledMessage?: string;
  /**
   * When true, allow submitting with no typed text and no files — for surfaces
   * where staged selections elsewhere carry the intent (e.g. picked components).
   */
  allowEmptySubmit?: boolean;
  /** External context items (e.g. selected ads) rendered as context pills alongside files. */
  contextItems?: ContextItem[];
  /** Called when the user removes a context item pill. */
  onRemoveContextItem?: (id: string) => void;
  /** Placeholder shown when contextItems are present but no text has been typed. */
  contextPlaceholder?: string;
  /**
   * When set, the default submit chevron is replaced by the override's
   * secondary + primary CTAs. The moment the user starts typing, the override
   * cluster collapses to a single "Skip and send" button that fires the
   * normal onSubmit (typed text bypasses the override).
   */
  submitOverride?: SubmitOverride | null;
  /**
   * Initial textarea value, applied only on first mount. Use this (instead of
   * imperatively calling `applySuggestion` after mount) when the input should
   * render pre-filled, so the focused/has-content styling is present on the
   * first paint and doesn't animate in.
   */
  defaultValue?: string;
  /**
   * Fires whenever the composer's text changes — typing, an imperatively
   * applied suggestion, or the clear that follows a submit. Lets a parent
   * observe the in-progress text (e.g. to persist it as a draft) without
   * taking ownership of the value.
   */
  onValueChange?: (value: string) => void;
  /**
   * When true, the placeholder crossfades as the `placeholder` prop changes
   * (e.g. previewing a hovered home suggestion) instead of using the static
   * native textarea placeholder, and the box animates its height as content
   * changes. Opt-in; defaults to the unanimated native behavior.
   */
  animatePlaceholder?: boolean;
  /**
   * Image/file sources offered beyond the built-in file upload (content
   * library, catalog assets, …). Each is a self-contained picker resolving to
   * `FileInput[]`; the composer collects them into the same `files` state as
   * upload/paste/drop (subject to the shared attachment limit). Callers declare which
   * sources a surface offers.
   */
  attachmentSources?: AttachmentSource[];
  /**
   * Additional toolbar actions appended after the built-in actions, described
   * as data. The toolbar folds 2+ actions behind a "+" menu; a lone action
   * renders inline. For image/file pickers, prefer `attachmentSources` — this
   * is for arbitrary actions (toggles, non-file pickers).
   */
  leftActionItems?: ComposerLeftAction[];
  /** Force the stacked layout instead of expanding once the text wraps. */
  stackControls?: boolean;
  /** Minimum visible text rows (default 1). Pair values above 1 with `stackControls`. */
  minRows?: number;
  /**
   * When true, the AI disclosure renders below the input. Leave off when a
   * parent surface already renders its own disclosure (e.g.
   * CollapsibleEditorChat).
   */
  showDisclaimer?: boolean;
}

export interface CompactChatInputRef {
  focus: () => void;
  setValue: (text: string) => void;
  submit: () => void;
  setFiles: (files: ComposerFile[]) => void;
  openFilePicker: () => void;
  /** Set textarea value, focus, and place caret at end. Does not submit. */
  applySuggestion: (text: string) => void;
  /**
   * Add text after whatever is already typed, focus, and place caret at end.
   * Does not submit — for collecting several things into one message.
   */
  appendText: (text: string) => void;
}

export const CompactChatInput = forwardRef<
  CompactChatInputRef,
  CompactChatInputProps
>(
  (
    {
      placeholder = "What edits would you like to make?",
      onSubmit,
      validateSubmit,
      autoFocus,
      variant = "default",
      isLoading = false,
      dictationContextKey,
      onCancel,
      actions,
      showActionsWhenIdle,
      loadingPreviewText,
      promptBg,
      banner,
      topContent,
      contextChips,
      onPromptChipsReadyChange,
      onActiveChange,
      isDisabled,
      leftActions,
      submitDisabledMessage,
      allowEmptySubmit = false,
      contextItems,
      onRemoveContextItem,
      contextPlaceholder = "Create something inspired by these…",
      submitOverride,
      defaultValue,
      onValueChange,
      animatePlaceholder = false,
      attachmentSources = EMPTY_ATTACHMENT_SOURCES,
      leftActionItems,
      stackControls = false,
      minRows = 1,
      showDisclaimer,
    },
    ref,
  ) => {
    const [value, setValue] = useState(defaultValue ?? "");
    const [isFocused, setIsFocused] = useState(false);
    const [isMultiline, setIsMultiline] = useState(false);
    // Only used when `animatePlaceholder` is true
    const [animatedHeight, setAnimatedHeight] = useState<number>();
    const prefersReducedMotion = useReducedMotion();
    const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    // `mirrorRef` is an off-screen probe used to count wrapped lines.
    const contentRef = useRef<HTMLDivElement>(null);
    const leftControlsRef = useRef<HTMLDivElement | null>(null);
    const rightControlsRef = useRef<HTMLDivElement | null>(null);
    // The clusters remount when the layout flips, so the resize observer below
    // follows them through state; measurements read the refs, which are never
    // stale.
    const [leftControlsEl, setLeftControlsEl] = useState<HTMLDivElement | null>(
      null,
    );
    const [rightControlsEl, setRightControlsEl] =
      useState<HTMLDivElement | null>(null);
    const leftControlsCallbackRef = useCallback((el: HTMLDivElement | null) => {
      leftControlsRef.current = el;
      setLeftControlsEl(el);
    }, []);
    const rightControlsCallbackRef = useCallback(
      (el: HTMLDivElement | null) => {
        rightControlsRef.current = el;
        setRightControlsEl(el);
      },
      [],
    );
    const mirrorRef = useRef<HTMLDivElement>(null);

    const {
      files,
      setFiles,
      addRawFiles,
      addFileInputs,
      clearFiles,
      isProcessing: isFileProcessing,
      handlePaste,
      dropzoneRootProps,
      isDragActive,
      uploadsInFlight,
      removeAttachments,
      retryAttachment,
    } = useComposerAttachments({ isDisabled, isLoading });

    // Prefer an explicit caller-provided message; otherwise tell the user we're
    // waiting on file uploads so the disabled submit button isn't a mystery.
    // Gating submission on this also keeps a message from going out without its
    // attachments — raw-file processing only appends to `files` once its await
    // resolves.
    const effectiveSubmitDisabledMessage =
      submitDisabledMessage ??
      (isFileProcessing ? "Waiting for files to finish uploading…" : undefined);

    const valueRef = useRef(value);
    valueRef.current = value;
    const filesRef = useRef(files);
    filesRef.current = files;
    const promptBoxRef = useRef<HTMLDivElement>(null);

    // Every write to the text goes through here: it keeps `valueRef` usable
    // before the re-render lands (handleSubmit reads it synchronously) and is
    // the single place `onValueChange` fires from.
    const updateValue = useCallback(
      (next: string) => {
        setValue(next);
        valueRef.current = next;
        onValueChange?.(next);
      },
      [onValueChange],
    );

    const showLoadingPreview = Boolean(isLoading && loadingPreviewText);
    // Shared with the loading preview, so swapping one for the other keeps the field's height.
    const fieldMinHeight =
      minRows > 1 ? `${minRows * TEXTAREA_LINE_HEIGHT_PX}px` : undefined;
    const dictation = useComposerDictation({
      contextKey: dictationContextKey,
      textareaRef,
      value,
      // Submit gates (agent turn, file upload) keep send disabled; they must not abort a take.
      disabled: Boolean(isDisabled || showLoadingPreview),
    });
    const dictationBusyRef = useRef(false);
    dictationBusyRef.current = dictation.busy;

    useEffect(
      () => () => {
        if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
      },
      [],
    );

    // A textarea that mounts with text puts the caret at position 0, so typing
    // into a restored `defaultValue` (e.g. a persisted draft) would insert
    // before it. Start at the end instead, where the user left off. Reads the
    // node rather than the prop so it stays a mount-only concern.
    useEffect(() => {
      const el = textareaRef.current;
      if (!el?.value) return;
      el.setSelectionRange(el.value.length, el.value.length);
    }, []);

    // Track the prompt box's natural height so a clipping wrapper can animate
    // between heights as content changes (e.g. a suggestion populating the
    // input), instead of letting it snap.
    useLayoutEffect(() => {
      if (!animatePlaceholder) return;
      const box = promptBoxRef.current;
      if (!box) return;
      const measure = () => setAnimatedHeight(box.offsetHeight);
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(box);
      return () => observer.disconnect();
    }, [animatePlaceholder]);

    const handleSubmit = useCallback(() => {
      if (
        dictation.busy ||
        isLoading ||
        isDisabled ||
        effectiveSubmitDisabledMessage
      )
        return;
      const trimmed = valueRef.current.trim();
      if (!trimmed && filesRef.current.length === 0 && !allowEmptySubmit)
        return;
      // Let the consumer veto the submit (e.g. a required selection is missing)
      // before we fire onSubmit or clear the input — otherwise the user loses
      // their typed prompt on a rejected submit.
      if (validateSubmit && !validateSubmit()) return;
      onSubmit(trimmed, filesRef.current);
      dictation.trackSubmit();
      updateValue("");
      clearFiles();
    }, [
      dictation.busy,
      dictation.trackSubmit,
      onSubmit,
      validateSubmit,
      isLoading,
      isDisabled,
      effectiveSubmitDisabledMessage,
      allowEmptySubmit,
      clearFiles,
      updateValue,
    ]);

    // Waits a frame so the value lands before the caret moves.
    const focusAtEnd = useCallback((caret: number) => {
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(caret, caret);
      });
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => textareaRef.current?.focus(),
        setValue: updateValue,
        submit: () => handleSubmit(),
        setFiles,
        openFilePicker: () => fileInputRef.current?.click(),
        applySuggestion: (text: string) => {
          updateValue(text);
          focusAtEnd(text.length);
        },
        appendText: (text: string) => {
          // A rule between entries, so several appended in a row read as
          // separate asks rather than one run-on message.
          const typed = valueRef.current.trimEnd();
          const next = typed ? `${typed}\n\n---\n\n${text}` : text;
          updateValue(next);
          focusAtEnd(next.length);
        },
      }),
      [focusAtEnd, handleSubmit, setFiles, updateValue],
    );

    const hasContext = Boolean(contextItems && contextItems.length > 0);
    const hasContent =
      value.trim().length > 0 ||
      files.length > 0 ||
      uploadsInFlight.size > 0 ||
      hasContext;
    // The focused styling (white inner, grey ring) applies whenever the input
    // is focused OR holds content — and stays put once content is present, even
    // after blur, per the Surfboard design.
    const isActive = isFocused || hasContent;

    const isReadyForPromptChips =
      isFocused &&
      !value &&
      files.length === 0 &&
      !isLoading &&
      !isFileProcessing;

    useEffect(() => {
      onPromptChipsReadyChange?.(isReadyForPromptChips);
    }, [isReadyForPromptChips, onPromptChipsReadyChange]);

    useEffect(() => {
      onActiveChange?.(isActive);
    }, [isActive, onActiveChange]);

    // Reset on unmount so a consumer-owned ring that outlives this input (e.g.
    // the collapsed overlay swapping in a loading shimmer) doesn't stay stuck
    // in the focused styling.
    useEffect(() => () => onActiveChange?.(false), [onActiveChange]);

    // Focus before uploading so completion cannot steal focus from the file popover.
    const refocusTextArea = useCallback(() => {
      textareaRef.current?.focus();
    }, []);

    const attachRawFiles = useCallback(
      async (rawFiles: File[]) => {
        refocusTextArea();
        await addRawFiles(rawFiles);
      },
      [addRawFiles, refocusTextArea],
    );

    const attachFileInputs = useCallback(
      (newFiles: ComposerFile[]) => {
        addFileInputs(newFiles);
        refocusTextArea();
      },
      [addFileInputs, refocusTextArea],
    );

    // Attachment sources (content library, catalog assets, …) all funnel their
    // picked files into the same `files` state as file upload/paste/drop via
    // `addFileInputs`. Their pickers' modals are rendered below, outside the
    // lazy "+" menu, so an in-flight selection survives the menu closing (and a
    // torn-down composer, e.g. CollapsibleEditorChat swapping in its collapsed
    // loading row mid-turn).
    const { actions: sourceActions, elements: sourceElements } =
      useAttachmentSources({
        sources: attachmentSources,
        onSelect: attachFileInputs,
        isDisabled: isLoading || isDisabled || isFileProcessing,
      });

    const handleFileSelect = useCallback(
      async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        if (!selectedFiles?.length) return;
        await attachRawFiles(Array.from(selectedFiles));
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      },
      [attachRawFiles],
    );

    const shouldShowActions =
      !isDisabled &&
      Boolean(actions) &&
      (showActionsWhenIdle || isFocused || hasContent);

    // The attach controls, described as data. ComposerLeftActions renders a
    // lone action (file upload only) as an inline icon button and folds 2+
    // (with attachment sources and any extra actions) behind a "+" menu.
    // Pickers/modals are mounted outside it (below) so an in-flight selection
    // survives the menu closing.
    const composerLeftActions: ComposerLeftAction[] = [
      buildAddFilesAction({
        onClick: () => fileInputRef.current?.click(),
        isDisabled: isLoading || isDisabled,
        isLoading: isFileProcessing,
      }),
      ...sourceActions,
      ...(leftActionItems ?? []),
    ];

    const plusButton = (
      <ComposerLeftActions
        actions={composerLeftActions}
        isDisabled={isDisabled}
      />
    );

    const overrideActive = Boolean(submitOverride) && !isLoading;
    const hasTypedContent = value.trim().length > 0 || files.length > 0;

    const placeholderText = (() => {
      if (isDisabled) return undefined;
      if (hasContext) return contextPlaceholder;
      if (overrideActive && submitOverride?.placeholder)
        return submitOverride.placeholder;
      return placeholder;
    })();

    const isCompactViewport = useCompactToolbarViewport();
    const hasCompactActions = Boolean(
      leftActionItems?.some(
        (action) => action.compactOnly || action.hasInlinePill,
      ),
    );

    // Via ref so measureMultiline stays stable for the one-time ResizeObserver.
    const placeholderRef = useRef(placeholderText);
    placeholderRef.current = placeholderText;

    // Decide whether to expand to the multiline layout (textarea full-width on
    // its own row, controls dropped to a row below) by measuring how many lines
    // the text wraps to *at the single-line width* — i.e. the width the text
    // has when it shares a row with the inline +/submit controls.
    //
    // We measure against an off-screen mirror sized to that single-line width
    // rather than the live textarea. The live textarea is full-width in the
    // multiline layout, so reading its height there would report fewer lines,
    // collapse the layout, re-wrap the text, and flip back — the exact feedback
    // loop that made the controls jitter around the line break. The mirror's
    // width never depends on the current mode, so the decision is stable.
    const measureMultiline = useCallback(() => {
      if (dictationBusyRef.current) return;
      const mirror = mirrorRef.current;
      const content = contentRef.current;
      if (!mirror || !content) return;
      // Collapsing controls must not make an automatically stacked composer
      // unstack and restore those same controls in a resize loop.
      if (
        hasCompactActions &&
        (isCompactViewport || content.clientWidth < COMPACT_TOOLBAR_WIDTH)
      ) {
        setIsMultiline(true);
        return;
      }
      const singleLineTextWidth = getSingleLineTextWidth({
        contentWidth: content.clientWidth,
        // Unclipped: stacked, the left controls may be scrolling, and the
        // decision needs what they'd take inline, not what they get now.
        leftControlsWidth: getUnclippedWidth(leftControlsRef.current),
        rightControlsWidth: rightControlsRef.current?.offsetWidth ?? 0,
      });
      // Controls width is identical in both layouts, so this can't oscillate.
      if (singleLineTextWidth < MIN_INLINE_TEXT_WIDTH_PX) {
        setIsMultiline(true);
        return;
      }

      const wrapsBesideControls = (text: string | undefined) => {
        if (!text) return false;
        mirror.style.width = `${Math.max(singleLineTextWidth, 1)}px`;
        // Trailing ZWSP so a value ending in "\n" counts its final empty line.
        mirror.textContent = `${text}\u200b`;
        return mirror.scrollHeight > TEXTAREA_LINE_HEIGHT_PX * 1.5;
      };

      // Stay stacked so the first keystroke can't reshape the composer;
      // animated placeholders opt out.
      const placeholderTooLong =
        !animatePlaceholder && wrapsBesideControls(placeholderRef.current);

      setIsMultiline(
        placeholderTooLong || wrapsBesideControls(valueRef.current),
      );
    }, [animatePlaceholder, hasCompactActions, isCompactViewport]);

    // Re-measure whenever the text or the inline controls' width can change
    // (controls toggle with focus/content/override/loading state).
    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    useLayoutEffect(() => {
      measureMultiline();
    }, [
      measureMultiline,
      value,
      placeholderText,
      shouldShowActions,
      overrideActive,
      hasTypedContent,
      isLoading,
      isDisabled,
    ]);

    // And whenever the input or either control cluster resizes: the window or
    // container, or a chip mounting or expanding (e.g. a toggle turned on from
    // the "+" menu).
    useEffect(() => {
      const content = contentRef.current;
      if (!content || typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(() => measureMultiline());
      for (const element of [content, leftControlsEl, rightControlsEl]) {
        if (element) observer.observe(element);
      }
      return () => observer.disconnect();
    }, [measureMultiline, leftControlsEl, rightControlsEl]);

    const isStacked = stackControls || isMultiline || dictation.busy;

    const overrideCluster = submitOverride ? (
      <Row
        gap="6px"
        align="center"
        sx={{
          // pill the override buttons to match the default chevron
          "& button": { borderRadius: "full" },
        }}
      >
        {hasTypedContent ? (
          <Button
            variant="primary"
            isDisabled={isDisabled}
            onClick={handleSubmit}
          >
            Skip and send
          </Button>
        ) : (
          <>
            {submitOverride.secondaryActions.map((action) => (
              <WithModeTransitionTooltip
                key={action.id}
                target={action.modeTransitionTarget}
              >
                <Button
                  variant={action.variant ?? "secondary"}
                  isDisabled={action.isDisabled || isDisabled}
                  onClick={action.onClick}
                >
                  {action.label}
                </Button>
              </WithModeTransitionTooltip>
            ))}
            <WithModeTransitionTooltip
              target={submitOverride.primary.modeTransitionTarget}
            >
              <Button
                variant="primary"
                isDisabled={submitOverride.primary.isDisabled || isDisabled}
                onClick={submitOverride.primary.onClick}
              >
                {submitOverride.primary.label}
              </Button>
            </WithModeTransitionTooltip>
          </>
        )}
      </Row>
    ) : null;

    const submitButton = overrideActive ? (
      overrideCluster
    ) : (
      <PromptInputSubmitButton
        hasContent={hasContent || allowEmptySubmit}
        isDisabled={isDisabled || Boolean(effectiveSubmitDisabledMessage)}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        disabledMessage={effectiveSubmitDisabledMessage}
      />
    );

    const textareaElement = (
      <chakra.textarea
        ref={textareaRef}
        autoFocus={autoFocus}
        readOnly={isDisabled || dictation.busy}
        flex={1}
        minWidth={0}
        bg="transparent"
        border="none"
        outline="none"
        fontSize="14px"
        fontWeight={300}
        lineHeight="20px"
        // When `animatePlaceholder` is true, the native placeholder is suppressed
        // and rendered as a crossfading overlay (AnimatedPlaceholder) instead.
        placeholder={animatePlaceholder ? undefined : placeholderText}
        value={value}
        rows={1}
        resize="none"
        minHeight={fieldMinHeight}
        maxHeight="480px"
        overflow="auto"
        // Same disabled read as LLMChatTextArea's `_disabled`, via the token
        // rather than a blanket opacity. `_disabled` can't be used here: the
        // field is `readOnly`, which never matches `:disabled`.
        color={isDisabled ? "text.disabled" : undefined}
        pointerEvents={isDisabled ? "none" : undefined}
        tabIndex={isDisabled ? -1 : undefined}
        sx={{
          fieldSizing: "content",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,0.15)",
            borderRadius: "3px",
          },
        }}
        onChange={(e) => {
          updateValue(e.target.value);
        }}
        onKeyDown={(e) => {
          if (handleAltEnterNewline(e)) return;
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
          }
        }}
        onPaste={handlePaste}
        _placeholder={{ color: "text.disabled", userSelect: "none" }}
      />
    );

    const textareaContent = animatePlaceholder ? (
      <Box position="relative" display="flex" flex={1} minWidth={0}>
        {textareaElement}
        <AnimatedPlaceholder
          text={value || dictation.busy ? undefined : placeholderText}
          alignTop={minRows > 1}
        />
      </Box>
    ) : (
      textareaElement
    );

    // The text field. `flex={1}` fills the row beside the controls in the
    // single-line layout; in the multiline layout the parent Column stretches
    // it to the full width so the top lines extend edge-to-edge.
    const inputField = showLoadingPreview ? (
      <Box flex={1} minWidth={0} minHeight={fieldMinHeight} py="2px">
        <TextWithTooltip fontWeight="medium" {...textShimmer}>
          {loadingPreviewText}
        </TextWithTooltip>
      </Box>
    ) : (
      <Box display="flex" flex={1} minWidth={0} width="100%">
        {textareaContent}
      </Box>
    );

    const stackedActions = isStacked && shouldShowActions ? actions : null;

    // Stacked, the controls row is the toolbar: its conditional controls scroll
    // sideways once they outgrow it, so dictation and submit stay anchored at
    // the end. Inline they never shrink; the layout stacks instead.
    const leftControls = (
      <Row
        ref={leftControlsCallbackRef}
        gap="8px"
        flexShrink={isStacked ? 1 : 0}
        minWidth={isStacked ? 0 : undefined}
        align="center"
        onClick={(e) => e.stopPropagation()}
      >
        {plusButton}
        {(leftActions || stackedActions) && (
          <ComposerToolbarScroller>
            {leftActions}
            {stackedActions}
          </ComposerToolbarScroller>
        )}
      </Row>
    );

    const rightControls = (
      <Row
        ref={rightControlsCallbackRef}
        gap="8px"
        flexShrink={0}
        align="center"
        onClick={(e) => e.stopPropagation()}
      >
        {!isStacked && shouldShowActions && actions}
        <DictationControl dictation={dictation} />
        {submitButton}
      </Row>
    );

    const isElevated = variant === "elevated";

    // In the elevated variant the inner box sits inside a rounded wrapper, so
    // it always uses the concentric radius — never the idle pill, which would
    // bulge unevenly against the wrapper's rounded-rectangle corners. Shared
    // with the height-animating clip wrapper so their corners stay aligned.
    const inputBoxRadius =
      isElevated || isActive || isStacked
        ? `${COMPOSER_BOX_RADIUS_PX}px`
        : "9999px";

    const input = (
      <Column>
        <Box
          ref={promptBoxRef}
          {...{ [COMPOSER_BOX_ATTR]: true }}
          // Disabled, the elevated composer flattens to one grey surface and
          // drops its border, letting the ring wrapper below fill to the same
          // grey. Elevated only: without that wrapper the border is this box's
          // only separator from the page, so it has to stay.
          bg={
            isElevated && isDisabled
              ? "base.background"
              : isDragActive
                ? "primary.background"
                : isActive
                  ? "white"
                  : (promptBg ?? "base.background")
          }
          borderRadius={inputBoxRadius}
          borderWidth="hairline"
          borderStyle="solid"
          borderColor={
            isElevated && isDisabled
              ? "base.borderTransparent"
              : isDragActive
                ? "primary.base"
                : isActive
                  ? "gray.300"
                  : "base.divider"
          }
          minHeight="56px"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          pl="12px"
          pr="12px"
          py="12px"
          transitionProperty="background-color, border-color"
          transitionDuration="normal"
          transitionTimingFunction="easeOutQuad"
          // Elevated: the ring wrapper owns hover and click-to-focus for this box.
          _hover={isElevated || isDisabled ? undefined : { bg: "white" }}
          // Portal'd menus drop :hover/:focus-within while open — pin on the
          // trigger's aria-expanded.
          sx={
            isDisabled
              ? undefined
              : {
                  '&:has([aria-expanded="true"])': {
                    bg: "white",
                    borderColor: "gray.300",
                  },
                }
          }
          onClick={
            isElevated || isDisabled
              ? undefined
              : () => textareaRef.current?.focus()
          }
          onFocusCapture={() => {
            if (blurTimeoutRef.current) {
              clearTimeout(blurTimeoutRef.current);
              blurTimeoutRef.current = null;
            }
            setIsFocused(true);
          }}
          onBlurCapture={() => {
            blurTimeoutRef.current = setTimeout(() => {
              setIsFocused(false);
            }, 0);
          }}
        >
          {(files.length > 0 ||
            uploadsInFlight.size > 0 ||
            Boolean(contextItems?.length) ||
            Boolean(contextChips?.length) ||
            topContent) && (
            <Column
              py={1}
              mb={1}
              gap={2}
              // Lined up with the text, which stacked controls inset.
              px={isStacked ? 2 : 0}
              onClick={(event) => event.stopPropagation()}
            >
              <ContextRow
                contextItems={contextItems}
                onRemoveContextItem={onRemoveContextItem}
              />
              <ComposerFiles
                files={files}
                uploadsInFlight={uploadsInFlight}
                removeAttachments={removeAttachments}
                retryAttachment={retryAttachment}
              />
              {topContent}
              {contextChips?.length ? (
                <ComposerContextChipRow
                  layout="row"
                  chips={contextChips}
                  onRemove={(chip) => chip.onRemove()}
                />
              ) : null}
            </Column>
          )}
          <Box
            as="input"
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
          {/* Off-screen probe used by measureMultiline to count wrapped lines
              at the single-line text width. Mirrors the textarea typography and
              wrapping; width + text are set imperatively. */}
          <Box
            ref={mirrorRef}
            aria-hidden
            position="absolute"
            top={0}
            left={0}
            height="auto"
            visibility="hidden"
            pointerEvents="none"
            zIndex={-1}
            fontSize="14px"
            fontWeight={300}
            lineHeight="20px"
            whiteSpace="pre-wrap"
            sx={{ overflowWrap: "break-word", wordBreak: "break-word" }}
          />
          <Box
            ref={contentRef}
            width="100%"
            {...{ [COMPOSER_LAYOUT_ATTR]: isStacked ? "stacked" : "inline" }}
          >
            {/* The text field stays in this first row in both modes so it never
                remounts (which would drop focus mid-type). When the text wraps,
                the controls move out to a second row below and the field gets
                the full width. */}
            <Column gap={isStacked ? 2 : 0}>
              <Row
                align="center"
                gap={2}
                // Inset so the first character aligns with the glyphs below.
                px={isStacked ? 2 : 0}
              >
                {!isStacked && leftControls}
                {inputField}
                {!isStacked && rightControls}
              </Row>
              <DictationToolbar dictation={dictation} textareaRef={textareaRef}>
                {isStacked && (
                  <ComposerToolbar collapseActions={hasCompactActions}>
                    {leftControls}
                    {rightControls}
                  </ComposerToolbar>
                )}
              </DictationToolbar>
            </Column>
          </Box>
        </Box>
      </Column>
    );

    // Wrap the input in a height-clipping container so the box grows/shrinks
    // smoothly (e.g. when a suggestion populates it).
    const animatedInput = animatePlaceholder ? (
      <Box
        overflow="hidden"
        height={animatedHeight !== undefined ? `${animatedHeight}px` : "auto"}
        borderRadius={inputBoxRadius}
        transitionProperty="height"
        transitionDuration={prefersReducedMotion ? "0ms" : "normal"}
        transitionTimingFunction="easeOutQuad"
      >
        {input}
      </Box>
    ) : (
      input
    );

    const disclosure = showDisclaimer ? (
      <LlmDisclaimer px={4} pt={1.5} />
    ) : null;

    if (isElevated) {
      return (
        <>
          <Column {...dropzoneRootProps}>
            {banner && <Box px="4px">{banner}</Box>}
            <Box
              {...{ [COMPOSER_RING_ATTR]: true }}
              // Disabled, the band stops being a hit area: it fills to the same
              // grey as the box (so the ring disappears into one flat surface),
              // flattens to the lighter shadow, and takes no state styling.
              bg={
                isDisabled
                  ? "base.background"
                  : hasContent
                    ? "base.backgroundHover"
                    : "white"
              }
              borderRadius={`${ELEVATED_WRAPPER_RADIUS_PX}px`}
              p={`${ELEVATED_WRAPPER_PADDING_PX}px`}
              transitionProperty="background-color"
              transitionDuration="normal"
              transitionTimingFunction="easeOutQuad"
              shadow={isDisabled ? "buttonShadowLight" : "md"}
              _hover={
                isDisabled
                  ? undefined
                  : {
                      bg: "base.backgroundHover",
                      // A drag keeps :hover on the element under the cursor.
                      ...(isDragActive
                        ? {}
                        : { [COMPOSER_BOX_SELECTOR]: LIT_BOX }),
                    }
              }
              _focusWithin={
                isDisabled ? undefined : { bg: "base.backgroundHover" }
              }
              // Same open-menu pin as the inner box.
              sx={
                isDisabled
                  ? undefined
                  : {
                      '&:has([aria-expanded="true"])': {
                        bg: "base.backgroundHover",
                      },
                    }
              }
              onClick={
                isDisabled ? undefined : () => textareaRef.current?.focus()
              }
            >
              {animatedInput}
            </Box>
            {disclosure}
          </Column>
          {/* Mounted outside the (lazy) "+" menu so an in-flight selection
              survives its trigger unmounting when the menu closes. */}
          {sourceElements}
        </>
      );
    } else {
      return (
        <>
          <Column {...dropzoneRootProps}>
            {banner && <Box px="4px">{banner}</Box>}
            {animatedInput}
            {disclosure}
          </Column>
          {/* Mounted outside the (lazy) "+" menu so an in-flight selection
              survives its trigger unmounting when the menu closes. */}
          {sourceElements}
        </>
      );
    }
  },
);

CompactChatInput.displayName = "CompactChatInput";

/**
 * Placeholder overlay that fades each new string in as `text` changes
 */
const AnimatedPlaceholder = ({
  text,
  alignTop = false,
}: {
  text?: string;
  /** Sit on the first line; use when the textarea spans 2+ rows. */
  alignTop?: boolean;
}) => {
  const [initialText] = useState(text);
  const [hasChanged, setHasChanged] = useState(false);
  if (!hasChanged && text !== initialText) setHasChanged(true);

  return (
    <Box
      position="absolute"
      inset={0}
      pointerEvents="none"
      overflow="hidden"
      sx={{ "@keyframes placeholder-fade-in": { from: { opacity: 0 } } }}
    >
      {text && (
        <Box
          // Remounts per string so the fade restarts; the old string leaves at once.
          key={text}
          position="absolute"
          inset={0}
          display="flex"
          alignItems={alignTop ? "flex-start" : "center"}
          animation={
            hasChanged
              ? "placeholder-fade-in var(--chakra-transition-duration-normal) var(--chakra-transition-easing-easeOutQuad)"
              : undefined
          }
        >
          <Box
            isTruncated
            width="100%"
            fontSize="14px"
            lineHeight="20px"
            fontWeight={300}
            color="text.disabled"
            userSelect="none"
          >
            {text}
          </Box>
        </Box>
      )}
    </Box>
  );
};

/**
 * Row of stacked image thumbnails that overlap with alternating rotation.
 * On hover, pills spread apart and straighten. Individual pills show a
 * dark preview popover on hover.
 */
const POPOVER_OPEN_DELAY_MS = 400;
const POPOVER_SKIP_DELAY_WINDOW_MS = 500;

export const ContextRow = ({
  contextItems,
  onRemoveContextItem,
}: {
  contextItems?: ContextItem[];
  onRemoveContextItem?: (id: string) => void;
}) => {
  const visibleLimit = 15;
  const visibleContextCount = Math.min(contextItems?.length ?? 0, visibleLimit);
  const [isRowHovered, setIsRowHovered] = useState(false);
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);
  const [openPopoverKey, setOpenPopoverKey] = useState<string | null>(null);

  const lastClosedAtRef = useRef(0);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
    },
    [],
  );

  const handlePillEnter = useCallback((key: string) => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    const skipDelay =
      Date.now() - lastClosedAtRef.current < POPOVER_SKIP_DELAY_WINDOW_MS;
    if (skipDelay) {
      setOpenPopoverKey(key);
    } else {
      openTimerRef.current = setTimeout(() => {
        setOpenPopoverKey(key);
      }, POPOVER_OPEN_DELAY_MS);
    }
  }, []);

  const handlePillLeave = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    setOpenPopoverKey((current) => {
      if (current !== null) lastClosedAtRef.current = Date.now();
      return null;
    });
  }, []);

  // Keep selected context images in one gallery so arrow navigation follows the row.
  const galleryImages: GalleryImage[] = [];
  const contextItemGalleryIndices: Array<number | undefined> = [];
  contextItems?.forEach((item) => {
    if (item.imageSrc) {
      contextItemGalleryIndices.push(galleryImages.length);
      galleryImages.push({ imageSrc: item.imageSrc, fileName: item.label });
    } else {
      contextItemGalleryIndices.push(undefined);
    }
  });
  const { openGallery, overlay: galleryOverlay } =
    useImageGalleryOverlay(galleryImages);

  if (!contextItems?.length) return null;

  return (
    <>
      <Row
        flexWrap="wrap"
        rowGap={3}
        width="100%"
        minWidth={0}
        align="center"
        position="relative"
        onMouseEnter={() => setIsRowHovered(true)}
        onMouseLeave={() => setIsRowHovered(false)}
      >
        {contextItems?.slice(0, visibleContextCount).map((item, index) => {
          const galleryIndex = contextItemGalleryIndices[index];
          return (
            <ContextItemPill
              key={item.id}
              item={item}
              index={index}
              isRowHovered={isRowHovered || isOverflowOpen}
              isPopoverOpen={openPopoverKey === item.id}
              onPillEnter={() => handlePillEnter(item.id)}
              onPillLeave={handlePillLeave}
              onRemove={
                onRemoveContextItem
                  ? () => onRemoveContextItem(item.id)
                  : undefined
              }
              onImageClick={
                galleryIndex !== undefined
                  ? () => openGallery(galleryIndex)
                  : undefined
              }
            />
          );
        })}
        <Box ml={1} flexShrink={0}>
          <AttachmentOverflow
            onOpenChange={setIsOverflowOpen}
            items={[
              ...(contextItems ?? [])
                .slice(visibleContextCount)
                .map((item, offset) => ({
                  id: item.id,
                  label: item.label,
                  imageSrc: item.imageSrc,
                  onPreview:
                    contextItemGalleryIndices[visibleContextCount + offset] !==
                    undefined
                      ? () =>
                          openGallery(
                            contextItemGalleryIndices[
                              visibleContextCount + offset
                            ] ?? 0,
                          )
                      : undefined,
                  onRemove: onRemoveContextItem
                    ? () => onRemoveContextItem(item.id)
                    : undefined,
                })),
            ]}
          />
        </Box>
      </Row>
      {galleryOverlay}
    </>
  );
};

const ContextItemPill = ({
  item,
  index,
  isRowHovered,
  isPopoverOpen,
  onPillEnter,
  onPillLeave,
  onRemove,
  onImageClick,
}: {
  item: ContextItem;
  index: number;
  isRowHovered: boolean;
  isPopoverOpen?: boolean;
  onPillEnter?: () => void;
  onPillLeave?: () => void;
  onRemove?: () => void;
  onImageClick?: () => void;
}) => (
  <BasePill
    thumbnail={item.thumbnail}
    popoverPreview={item.popoverPreview}
    label={item.label}
    index={index}
    isRowHovered={isRowHovered}
    isPopoverOpen={isPopoverOpen}
    onPillEnter={onPillEnter}
    onPillLeave={onPillLeave}
    onRemove={onRemove}
    onImageClick={onImageClick}
    bg="gray.100"
  />
);

const WithModeTransitionTooltip = ({
  target,
  children,
}: {
  target: HoistSubmitOverride["primary"]["modeTransitionTarget"];
  children: React.ReactElement;
}) => {
  if (!target) return children;
  return (
    <Tooltip message={getModeTransitionTooltip(target)} placement="top">
      {children}
    </Tooltip>
  );
};
